import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth, useTheme, API } from '../App';
import { toast } from 'sonner';
import { 
  CircleDot, Sun, Moon, ArrowLeft, Bookmark, Trash2, Wand2,
  Plus
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Presets = () => {
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [presets, setPresets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const res = await fetch(`${API}/presets`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPresets(data);
      }
    } catch (err) {
      console.error('Failed to fetch presets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      const res = await fetch(`${API}/presets/${deleteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      
      if (res.ok) {
        setPresets(prev => prev.filter(p => p.preset_id !== deleteId));
        toast.success('Preset deleted');
      }
    } catch (err) {
      toast.error('Failed to delete preset');
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Gallery</span>
          </Link>

          <div className="flex items-center gap-2">
            <CircleDot className="w-6 h-6 text-primary" />
            <span className="font-heading text-xl">Energy Presets</span>
          </div>

          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleTheme}
            data-testid="presets-theme-toggle"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="font-heading text-4xl mb-2" data-testid="presets-title">Your Energy Presets</h1>
              <p className="text-muted-foreground">
                Save your favorite configurations to quickly generate similar art
              </p>
            </div>
            <Link to="/studio">
              <Button className="rounded-full gap-2" data-testid="create-in-studio-btn">
                <Wand2 className="w-5 h-5" />
                Create in Studio
              </Button>
            </Link>
          </div>

          {/* Presets List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : presets.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-card p-12 text-center"
              data-testid="empty-presets"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Bookmark className="w-10 h-10 text-primary" />
              </div>
              <h2 className="font-heading text-2xl mb-2">No presets yet</h2>
              <p className="text-muted-foreground mb-6">
                Create sacred geometry in the studio and save your favorite configurations as presets.
              </p>
              <Link to="/studio">
                <Button className="rounded-full" data-testid="empty-studio-btn">
                  Enter the Studio
                </Button>
              </Link>
            </motion.div>
          ) : (
            <div className="grid gap-4">
              {presets.map((preset, index) => (
                <motion.div
                  key={preset.preset_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-card p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
                  data-testid={`preset-card-${preset.preset_id}`}
                >
                  <div className="flex-1">
                    <h3 className="font-heading text-xl mb-1">{preset.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2 capitalize">
                      "{preset.intention}"
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">
                        {preset.tradition.replace('_', ' ')}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">
                        {preset.geometry_framework.replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary">
                        {preset.symmetry_type}-fold
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-secondary capitalize">
                        {preset.color_palette}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Link to="/studio">
                      <Button size="sm" className="rounded-full gap-1" data-testid={`use-preset-${preset.preset_id}`}>
                        <Wand2 className="w-4 h-4" />
                        Use
                      </Button>
                    </Link>
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      className="rounded-full"
                      onClick={() => setDeleteId(preset.preset_id)}
                      data-testid={`delete-preset-${preset.preset_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="mt-10 text-center text-sm text-muted-foreground">
            <p>Presets save your intention, tradition, geometry, symmetry, and color choices.</p>
            <p>Load them in the Studio to quickly generate similar sacred geometry.</p>
          </div>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Preset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this preset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-preset-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-preset-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Presets;
